import { Grid, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useRecoilValue } from "recoil";
import { AverageAndMedianRemunerationService } from "../../../../Component/Api/EmploymentEquity/AverageAndMedianRemuneration/AverageAndMedianRemunerationService";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption, SortDirections } from "../../../../Component/Model/Dto/ColumnSortOption";
import { EEAverageAndMedianRemuneration } from "../../../../Component/Model/Dto/EmploymentEquity/AverageAndMedianRemuneration/AverageAndMedianRemuneration";
import { ScorecardAtom } from "../../../../State/Scorecard.Atom";
import { TopAndBottomEarnersTableMapper } from "./AverageAndMedianRemunerationTableColumns";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { RemunerationDrilldownType } from "../../../../Component/Model/Enum/RemunerationDrilldownType";

interface IProps {
    getTop5Percent: boolean;
    drilldownType: RemunerationDrilldownType;
    isUpperRange?: boolean;
}

export const MedianRemunerationEarnerPopup = (props: IProps) => {
    const [entriesPerPage, setEntriesPerPage] = React.useState(10);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [topOrBottomEarnerData, setTopOrBottomEarnerData] = React.useState<EEAverageAndMedianRemuneration.TopAndBottomEarner[]>([]);
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const scorecard = useRecoilValue(ScorecardAtom);
    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage(entriesPerPage);
        setPageNumber(1);
    }

    const fetchData = async () => {

        setIsLoading(true);
        try {
            var request: EEAverageAndMedianRemuneration.TopAndBottomEarnerRequest = {
                scorecardId: scorecard.scorecardId,
                isTop5Percent: props.getTop5Percent,
                pageSize: entriesPerPage,
                pageNumber: pageNumber,
                columnSortOptions: columnSortOptions
            };

            let result;
            switch (props.drilldownType) {
                case RemunerationDrilldownType.Range:
                    if (props.isUpperRange !== undefined) {
                        result = await AverageAndMedianRemunerationService.TopOrBottomEarnersRange(request, props.isUpperRange);
                    }
                    break;
                case RemunerationDrilldownType.Median:
                    result = await AverageAndMedianRemunerationService.MedianEarners(request);
                    break;
                case RemunerationDrilldownType.Remuneration:
                    result = await AverageAndMedianRemunerationService.TopOrBottomEarners(request);
                    break;
                default:
                    break;
            }

            if (result?.data) {
                setTopOrBottomEarnerData(result.data.results);
                setTotalItemsCount(result.data.totalItemsCount);
                setFilteredItemsCount(result.data.filteredItemsCount);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    };

    const topAndBottomEarnersTableData = TopAndBottomEarnersTableMapper(topOrBottomEarnerData);

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, columnSortOptions]);

    return (
        <Grid container spacing={1}>
            <MDTypography
                color="info"
                fontWeight="bold"
                style={{ paddingBottom: '10px', color: 'text.primary' }}
                >
                Employee Structure
            </MDTypography>
            <Typography
                variant="caption"
                fontWeight="400"
                color="#f57c00"
                style={{ fontSize: '1rem' }}>
                    Note that if there are several employees with the same remuneration, the software first sorts the list by their remuneration, then their name, then their surname to determine which specific employees to display.
            </Typography>
            <Grid item xs={12} mt={2}>
                <DataTable
                    key={pageNumber}
                    table={topAndBottomEarnersTableData}
                    entriesPerPage={{ defaultValue: entriesPerPage }}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemsCount}
                    totalItemsCount={totalItemsCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    isLoading={isLoading}
                    manualSorting={true}
                    columnSortingChanged={columnSortingChanged}
                ></DataTable>
            </Grid>
        </Grid>
    )
}
